'use client';

import React, { useState, useEffect, useRef } from 'react';

interface UseCase {
  id: number;
  domain: string;
  title: string;
  desc: string;
  complexity: 'Standard' | 'Complex' | 'Advanced';
}

const useCases: UseCase[] = [
  // Financial Planning (12)
  { id: 1, domain: 'Financial Planning', title: 'Annual Revenue Planning', desc: 'Top-down revenue target setting with bottom-up sales input, reconciliation, and approval workflow.', complexity: 'Standard' },
  { id: 2, domain: 'Financial Planning', title: 'Expense Planning by Cost Centre', desc: 'Departmental expense planning with budget pack distribution, variance tracking, and consolidation.', complexity: 'Standard' },
  { id: 3, domain: 'Financial Planning', title: 'Headcount and Personnel Cost Planning', desc: 'Position-level headcount planning with salary, bonus, and benefits cost derivation.', complexity: 'Complex' },
  { id: 4, domain: 'Financial Planning', title: 'Rolling 12-Month P&L Forecast', desc: 'Continuous P&L forecast updated monthly with actuals lock and forward-looking assumptions.', complexity: 'Complex' },
  { id: 5, domain: 'Financial Planning', title: 'Cash Flow Forecasting', desc: 'Indirect cash flow forecast derived from P&L plan with working capital adjustments.', complexity: 'Advanced' },
  { id: 6, domain: 'Financial Planning', title: 'Balance Sheet Planning', desc: 'Integrated balance sheet plan connected to P&L and cash flow with key ratio tracking.', complexity: 'Advanced' },
  { id: 7, domain: 'Financial Planning', title: 'Capital Expenditure Planning', desc: 'Project-level capex planning with depreciation scheduling and asset register integration.', complexity: 'Complex' },
  { id: 8, domain: 'Financial Planning', title: 'Profit Centre Profitability Planning', desc: 'Profitability planning at profit centre level with cost allocation and transfer pricing.', complexity: 'Complex' },
  { id: 9, domain: 'Financial Planning', title: 'Multi-Entity Consolidated Planning', desc: 'Group-level planning with entity submissions, intercompany elimination, and currency translation.', complexity: 'Advanced' },
  { id: 10, domain: 'Financial Planning', title: 'Budget vs Actual Variance Analysis', desc: 'Automated variance reporting with drill-through from summary to transaction level.', complexity: 'Standard' },
  { id: 11, domain: 'Financial Planning', title: 'Zero-Based Budgeting Model', desc: 'Activity-based budgeting model requiring justification of all cost lines from zero.', complexity: 'Advanced' },
  { id: 12, domain: 'Financial Planning', title: 'Financial Scenario Simulation', desc: 'Side-by-side scenario comparison for base case, upside, and downside financial plans.', complexity: 'Complex' },

  // Sales Planning (10)
  { id: 13, domain: 'Sales Planning', title: 'Sales Quota Setting and Distribution', desc: 'Top-down quota setting by region and product with bottom-up sales rep assignment.', complexity: 'Standard' },
  { id: 14, domain: 'Sales Planning', title: 'Pipeline-Based Revenue Forecast', desc: 'CRM pipeline data integrated into SAC for probability-weighted revenue forecasting.', complexity: 'Complex' },
  { id: 15, domain: 'Sales Planning', title: 'Territory Planning and Coverage Model', desc: 'Sales territory design with revenue potential analysis and rep capacity planning.', complexity: 'Complex' },
  { id: 16, domain: 'Sales Planning', title: 'Product Mix and Pricing Planning', desc: 'Revenue planning by product line with price assumption management and mix analysis.', complexity: 'Standard' },
  { id: 17, domain: 'Sales Planning', title: 'Sales Incentive Compensation Planning', desc: 'Commission and bonus plan modelling connected to sales quota and achievement data.', complexity: 'Complex' },
  { id: 18, domain: 'Sales Planning', title: 'Demand Forecasting for Sales Operations', desc: 'Statistical and judgement-based demand forecast for sales operations and supply alignment.', complexity: 'Advanced' },
  { id: 19, domain: 'Sales Planning', title: 'Customer Segment Revenue Planning', desc: 'Revenue planning segmented by customer tier, channel, and geography.', complexity: 'Standard' },
  { id: 20, domain: 'Sales Planning', title: 'New Product Launch Financial Planning', desc: 'Revenue and cost planning for new product launches with ramp-up assumptions.', complexity: 'Complex' },
  { id: 21, domain: 'Sales Planning', title: 'Sales and Operations Planning Alignment', desc: 'Connecting sales volume plans to operational capacity and financial targets.', complexity: 'Advanced' },
  { id: 22, domain: 'Sales Planning', title: 'Account-Level Revenue Planning', desc: 'Key account planning with individual account revenue targets and growth assumptions.', complexity: 'Standard' },

  // Supply Chain Planning (11)
  { id: 23, domain: 'Supply Chain Planning', title: 'Statistical Demand Planning', desc: 'Baseline demand forecast using historical data with statistical smoothing and seasonal adjustment.', complexity: 'Advanced' },
  { id: 24, domain: 'Supply Chain Planning', title: 'Inventory Level Planning', desc: 'Safety stock, reorder point, and inventory investment planning by product and location.', complexity: 'Complex' },
  { id: 25, domain: 'Supply Chain Planning', title: 'Procurement Budget Planning', desc: 'Category-level procurement budget with supplier pricing assumptions and volume drivers.', complexity: 'Standard' },
  { id: 26, domain: 'Supply Chain Planning', title: 'Production Capacity Planning', desc: 'Production line capacity planning with utilisation analysis and bottleneck identification.', complexity: 'Complex' },
  { id: 27, domain: 'Supply Chain Planning', title: 'Raw Material Requirements Planning', desc: 'Material requirements planning connected to production forecasts and inventory positions.', complexity: 'Advanced' },
  { id: 28, domain: 'Supply Chain Planning', title: 'Logistics Cost Planning', desc: 'Freight and logistics cost planning by lane, mode, and volume driver.', complexity: 'Standard' },
  { id: 29, domain: 'Supply Chain Planning', title: 'Supplier Performance and Cost Planning', desc: 'Supplier cost benchmarking and variance planning for procurement negotiations.', complexity: 'Complex' },
  { id: 30, domain: 'Supply Chain Planning', title: 'Distribution Network Planning', desc: 'Warehouse and distribution cost planning with network optimisation scenarios.', complexity: 'Advanced' },
  { id: 31, domain: 'Supply Chain Planning', title: 'Manufacturing Cost Planning', desc: 'Standard cost planning for manufactured products including direct and overhead costs.', complexity: 'Complex' },
  { id: 32, domain: 'Supply Chain Planning', title: 'Supply Chain Risk Scenario Planning', desc: 'Scenario modelling for supply disruption, price volatility, and lead time changes.', complexity: 'Advanced' },
  { id: 33, domain: 'Supply Chain Planning', title: 'COGS Planning and Gross Margin Analysis', desc: 'Cost of goods sold planning connected to sales volume and production cost drivers.', complexity: 'Standard' },

  // Enterprise Planning (17)
  { id: 34, domain: 'Enterprise Planning', title: 'Integrated Business Planning (IBP)', desc: 'Connecting financial, sales, and supply chain plans in a single planning process.', complexity: 'Advanced' },
  { id: 35, domain: 'Enterprise Planning', title: 'Strategic Plan Modelling (3–5 Year)', desc: 'Long-range financial modelling with strategic driver assumptions and scenario analysis.', complexity: 'Advanced' },
  { id: 36, domain: 'Enterprise Planning', title: 'Executive Dashboard for Planning', desc: 'C-suite planning dashboard with P&L, cash, and KPI summary across all planning dimensions.', complexity: 'Standard' },
  { id: 37, domain: 'Enterprise Planning', title: 'Driver-Based Corporate Planning Model', desc: 'Enterprise planning model driven by volume, price, productivity, and headcount assumptions.', complexity: 'Advanced' },
  { id: 38, domain: 'Enterprise Planning', title: 'Mergers and Acquisitions Planning', desc: 'M&A financial modelling with acquisition impact on P&L, balance sheet, and cash flow.', complexity: 'Advanced' },
  { id: 39, domain: 'Enterprise Planning', title: 'ESG and Sustainability Planning', desc: 'Carbon emission and sustainability KPI planning integrated with financial planning.', complexity: 'Complex' },
  { id: 40, domain: 'Enterprise Planning', title: 'IT Cost Planning and Allocation', desc: 'IT department cost planning with service-based allocation to business units.', complexity: 'Complex' },
  { id: 41, domain: 'Enterprise Planning', title: 'Real Estate and Facilities Planning', desc: 'Property cost planning with lease management and space utilisation drivers.', complexity: 'Standard' },
  { id: 42, domain: 'Enterprise Planning', title: 'R&D Investment Planning', desc: 'Research and development budget planning with project-level tracking and capitalisation rules.', complexity: 'Complex' },
  { id: 43, domain: 'Enterprise Planning', title: 'Marketing Budget Planning', desc: 'Campaign-level marketing budget with ROI tracking and channel allocation.', complexity: 'Standard' },
  { id: 44, domain: 'Enterprise Planning', title: 'Workforce Planning and Organisational Design', desc: 'Headcount planning across organisational layers with span of control and cost modelling.', complexity: 'Complex' },
  { id: 45, domain: 'Enterprise Planning', title: 'Shared Services Cost Allocation Planning', desc: 'Shared services cost planning with allocation methodology design and business unit charging.', complexity: 'Complex' },
  { id: 46, domain: 'Enterprise Planning', title: 'Tax Planning Integration', desc: 'Effective tax rate planning integrated with pre-tax P&L and deferred tax modelling.', complexity: 'Advanced' },
  { id: 47, domain: 'Enterprise Planning', title: 'Business Unit P&L Planning', desc: 'Divisional P&L planning with transfer pricing, shared cost allocation, and consolidation.', complexity: 'Complex' },
  { id: 48, domain: 'Enterprise Planning', title: 'Project Portfolio Planning', desc: 'Portfolio-level investment planning with prioritisation, phasing, and benefit tracking.', complexity: 'Complex' },
  { id: 49, domain: 'Enterprise Planning', title: 'Board Reporting Pack Automation', desc: 'Automated board reporting driven by the planning model with narrative and commentary.', complexity: 'Standard' },
  { id: 50, domain: 'Enterprise Planning', title: 'Planning Model Migration from SAP BPC', desc: 'Migration planning and execution from SAP BPC (NetWeaver or Standard) to SAC Planning.', complexity: 'Advanced' },
];

const domains = ['All', 'Financial Planning', 'Sales Planning', 'Supply Chain Planning', 'Enterprise Planning'];
const complexities = ['All', 'Standard', 'Complex', 'Advanced'];

const complexityColors: Record<string, string> = {
  Standard: '#E8F4E8',
  Complex: '#FFF3E0',
  Advanced: '#FCE8E8',
};

export default function UseCasesContent() {
  const [activeDomain, setActiveDomain] = useState('All');
  const [activeComplexity, setActiveComplexity] = useState('All');
  const sectionRef = useRef<HTMLDivElement>(null);

  const filtered = useCases.filter((uc) => {
    const matchDomain = activeDomain === 'All' || uc.domain === activeDomain;
    const matchComplexity = activeComplexity === 'All' || uc.complexity === activeComplexity;
    return matchDomain && matchComplexity;
  });

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el) => {
              (el as HTMLElement).classList.add('is-visible');
            });
          }
        });
      },
      { threshold: 0.02 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={sectionRef} className="py-12 px-6">
      <div className="max-w-container mx-auto">
        {/* Filters */}
        <div className="mb-8 reveal-on-scroll flex flex-col gap-4">
          <div>
            <div className="mono-label mb-2">Domain</div>
            <div className="flex flex-wrap gap-2">
              {domains.map((d) => (
                <button
                  key={d}
                  onClick={() => setActiveDomain(d)}
                  className={`tag-pill cursor-pointer transition-all duration-150 ${
                    activeDomain === d ? 'bg-primary text-white border-primary' : 'hover:border-primary'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="mono-label mb-2">Complexity</div>
            <div className="flex flex-wrap gap-2">
              {complexities.map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveComplexity(c)}
                  className={`tag-pill cursor-pointer transition-all duration-150 ${
                    activeComplexity === c ? 'bg-primary text-white border-primary' : 'hover:border-primary'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mono-label mb-6 reveal-on-scroll">{filtered.length} use cases</div>

        {/* Use cases grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((uc, i) => (
            <div
              key={uc.id}
              className={`use-case-card reveal-on-scroll reveal-delay-${Math.min((i % 4) + 1, 6)} border border-border p-6 bg-white`}
              style={{ borderRadius: '4px' }}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <span className="mono-label">{String(uc.id).padStart(2, '0')}</span>
                  <span className="uc-tag tag-pill">{uc.domain}</span>
                </div>
                <span
                  className="uc-tag tag-pill shrink-0"
                  style={{ backgroundColor: complexityColors[uc.complexity], borderColor: 'transparent' }}
                >
                  {uc.complexity}
                </span>
              </div>
              <h3 className="font-heading font-semibold text-sm mb-2 leading-snug">
                {uc.title}
              </h3>
              <p className="text-xs font-body leading-relaxed" style={{ color: 'var(--color-muted)' }}>
                {uc.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}